import type { BoxProps } from '@mui/material/Box';
import { varFade, MotionViewport } from '@/components/animate';
import Box from '@mui/material/Box';
import Container from '@mui/material/Container';
import Typography from '@mui/material/Typography';
import { m } from 'framer-motion';
import { Button, Grid, useTheme } from '@mui/material';
import { useRef, useEffect } from 'react';
import { GridTextSection } from '@/types/sections/GridTextSection';
import { CardHighlightsItem } from '@/types/CardHighlightsItem';
import { getCtaUrl } from '@/types/Cta';
import { Icon } from '@/components/Icon';
import { getIconName } from '@/components/Icon/Icon';

type GridTextProps = BoxProps & {
  section: GridTextSection;
};

export default function GridTextComponent({ sx, section, ...other }: GridTextProps) {
  const theme = useTheme();
  const gridRef = useRef<HTMLDivElement>(null);

  // Function to equalize heights
  useEffect(() => {
    if (!gridRef.current) return;
    
    const equalizeHeights = () => {
      const gridItems = gridRef.current?.querySelectorAll('.grid-item-paper');
      if (!gridItems?.length) return;
      
      // Reset heights to auto first to get natural heights
      gridItems.forEach((item) => {
        (item as HTMLElement).style.height = 'auto';
      });
      
      // Find the tallest item
      let maxHeight = 0;
      gridItems.forEach((item) => {
        maxHeight = Math.max(maxHeight, (item as HTMLElement).offsetHeight);
      });
      
      // Apply the height to all items
      if (maxHeight > 0) {
        gridItems.forEach((item) => {
          (item as HTMLElement).style.height = `${maxHeight}px`;
        });
      }
    };
    
    // Run on initial render and window resize
    equalizeHeights();
    window.addEventListener('resize', equalizeHeights);
    
    return () => {
      window.removeEventListener('resize', equalizeHeights);
    };
  }, [section]);

  return (
    <Box
      component="section"
      sx={[{ overflow: 'hidden' }, ...(Array.isArray(sx) ? sx : [sx])]}
      {...other}
    >
      <Container component={MotionViewport} sx={{ textAlign: 'center', py: { xs: 2, md: 10 } }}>
        {section?.small && (
          <m.div variants={varFade('inDown')}>
            <Typography variant="overline" sx={{ color: 'text.disabled' }}>
              {section?.small}
            </Typography>
          </m.div>
        )}

        {section?.title && (
          <m.div variants={varFade('inUp')}>
            <Typography variant="h2" sx={{ my: 3, color: 'primary.main' }}>
              {section?.title}
            </Typography>
          </m.div>
        )}

        {section?.subtitle && (
          <m.div variants={varFade('inUp')}>
            <Typography sx={{ mx: 'auto', maxWidth: 640, color: 'text.secondary' }}>
              {section?.subtitle}
            </Typography>
          </m.div>
        )}

        <m.div variants={varFade('inUp')}>
          <Grid 
            container 
            spacing={6}
            sx={{ width: 1, pt: 4 }}
            ref={gridRef}
          >
            {section?.cards?.map((item, index) => (
              <Grid key={`grid-item-${index}`} size={{ xs: 12, sm: 6, md: 6, }}>
                  <GridItem 
                    key={index} 
                    index={index} 
                    item={{ 
                      id: `carousel-item-${index}`,
                      ...item,
                    }} 
                  />
              </Grid>
            ))}
          </Grid>
        </m.div>
        
        {section?.cta1 && (
          <m.div variants={varFade('inUp')}>
            <Button size="large" variant="contained" color="primary"
              href={getCtaUrl(section.cta1)}
              sx={{ 
                mt: 4,
                borderRadius: 0,
              }}
              startIcon={ section.cta1.icon && (
                <Icon name={getIconName(section.cta1.icon, 'unknown')} color={theme.palette.common.white} />
                )}
              >
              {section?.cta1.title}
            </Button>
          </m.div>
        )}
      </Container>
    </Box>
  );
}

// ----------------------------------------------------------------------

type GridTextItemProps = {
  index: number;
  item: { id: string } & CardHighlightsItem;
};
function GridItem({ item, index }: GridTextItemProps) {
  return (
    <Box
      sx={{
        p: 3,
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'flex-start', // Aligns content at the top
        alignItems: 'center', // Centers all other content horizontally
        textAlign: 'center', // Ensures text is centered for other sections
      }}
    >
      {/* Icon Section */}
      <Box
        sx={{
          color: 'primary.main',
          mb: 2,
          height: 64, // Fixed height for icons
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
        }}
      >
        {item.icon && (
          <Icon name={getIconName(item.icon, 'unknown')} size="40" />
        )}
      </Box>

      {/* Title Section */}
      <Box
        sx={{
          mb: 2,
          height: 48, // Fixed height for titles
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
        }}
      >
        {item.title && (
          <Typography variant="h3" sx={{ color: 'primary.main' }}>
            {item.title}
          </Typography>
        )}
      </Box>

      {/* Highlights Section */}
      <Box
        sx={{
          flexGrow: 1, // Allows this section to grow and fill available space
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'flex-start',
          alignItems: 'center',
          width: '100%',
        }}
      >
        {item?.highlights?.map((highlight, idx) => (
          <Box
            key={`grid-highlight-${idx}`}
            sx={{
              flexDirection: 'column',
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              width: {
                xs: '90%', // 90% width for small devices (xs breakpoint)
                sm: '90%', // 90% width for small devices (sm breakpoint)
                md: '80%', // 80% width for medium devices (md breakpoint)
              },
              m: 1,
            }}
          >
            <Box
              sx={{
                display: 'flex',
                alignItems: 'flex-start',
                textAlign: 'left',
                width: '100%',
              }}
            >
              <Box sx={{ color: 'text.secondary', mr: 2 }}>
                <Icon name={getIconName(highlight.icon, 'unknown')} size="18"/>
              </Box>
              <Typography
                variant="body1"
                sx={{
                  color: 'text.secondary',
                  '& strong': { color: 'text.primary' },
                }}
              >
                {highlight.text || ''}
              </Typography>
            </Box>
          </Box>
        ))}
      </Box>
    </Box>
  );
}